import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PhobosTaskInstanceService } from '../phobos-task-instance.service';

export type HtmlReportSupportingFileCountLoadStatus =
   | 'IDLE'
   | 'LOADING'
   | 'LOADED'
   | 'ERROR';

export interface HtmlReportSupportingFileCountState {
   status: HtmlReportSupportingFileCountLoadStatus;
   countsByRowId: ReadonlyMap<string, number>;
   errorStatus: number | null;
}

const INITIAL_STATE: HtmlReportSupportingFileCountState = {
   status: 'IDLE',
   countsByRowId: new Map<string, number>(),
   errorStatus: null
};

@Injectable({
   providedIn: 'root'
})
export class HtmlReportSupportingFileCountStoreService {
   private readonly stateSubject =
      new BehaviorSubject<HtmlReportSupportingFileCountState>(INITIAL_STATE);

   readonly state$ = this.stateSubject.asObservable();

   private activeKey: string | null = null;
   private loadingKey: string | null = null;
   private requestVersion = 0;
   private readonly pendingCounts = new Map<string, number>();

   constructor(private phobosTaskInstanceService: PhobosTaskInstanceService) {}

   load(taskInstanceId: number, pmsDataId: number): void {
      if (taskInstanceId <= 0 || pmsDataId <= 0) {
         this.reset();
         return;
      }

      const key = `${taskInstanceId}:${pmsDataId}`;

      if (this.loadingKey === key) {
         return;
      }

      if (this.activeKey !== key) {
         this.pendingCounts.clear();
      }

      const requestVersion = ++this.requestVersion;

      this.activeKey = key;
      this.loadingKey = key;

      this.stateSubject.next({
         status: 'LOADING',
         countsByRowId: new Map<string, number>(),
         errorStatus: null
      });

      this.phobosTaskInstanceService
         .getPmsRowSupportingFileCounts(taskInstanceId, pmsDataId)
         .subscribe({
            next: (counts) => {
               if (requestVersion !== this.requestVersion) {
                  return;
               }

               this.loadingKey = null;

               const countsByRowId = new Map<string, number>();

               for (const item of counts ?? []) {
                  if (item.targetRowId && item.fileCount > 0) {
                     countsByRowId.set(item.targetRowId, item.fileCount);
                  }
               }

               // Preserve successful uploads made while counts were loading.
               for (const [rowId, count] of this.pendingCounts) {
                  if (count > 0) {
                     countsByRowId.set(rowId, count);
                  } else {
                     countsByRowId.delete(rowId);
                  }
               }

               this.pendingCounts.clear();

               this.stateSubject.next({
                  status: 'LOADED',
                  countsByRowId,
                  errorStatus: null
               });
            },
            error: (error: HttpErrorResponse) => {
               if (requestVersion !== this.requestVersion) {
                  return;
               }

               this.loadingKey = null;

               // Retain counts confirmed by successful uploads, even if
               // the report-wide count request fails.
               const countsByRowId = new Map<string, number>();

               for (const [rowId, count] of this.pendingCounts) {
                  if (count > 0) {
                     countsByRowId.set(rowId, count);
                  }
               }

               this.pendingCounts.clear();

               this.stateSubject.next({
                  status: 'ERROR',
                  countsByRowId,
                  errorStatus: error.status
               });
            }
         });
   }

   setRowCount(
      taskInstanceId: number,
      pmsDataId: number,
      targetRowId: string,
      fileCount: number
   ): void {
      if (
         this.activeKey !== `${taskInstanceId}:${pmsDataId}` ||
         !targetRowId.trim() ||
         !Number.isInteger(fileCount) ||
         fileCount < 0
      ) {
         return;
      }

      const currentState = this.stateSubject.value;
      const countsByRowId = new Map(currentState.countsByRowId);
      const rowId = targetRowId.trim();

      if (currentState.status === 'LOADING') {
         this.pendingCounts.set(rowId, fileCount);
      }

      if (fileCount > 0) {
         countsByRowId.set(rowId, fileCount);
      } else {
         countsByRowId.delete(rowId);
      }

      this.stateSubject.next({
         ...currentState,
         countsByRowId
      });
   }

   reset(): void {
      ++this.requestVersion;

      this.activeKey = null;
      this.loadingKey = null;
      this.pendingCounts.clear();

      this.stateSubject.next({
         ...INITIAL_STATE,
         countsByRowId: new Map<string, number>()
      });
   }
}
